import * as React from 'react';

/*
 * Dock — the floating pill navigation (v2 design system).
 * Icon-only; the active item is filled with the brand color; nav items sit
 * left of the separator, actions (e.g. "+", More, live badge) to the right.
 * Contents are contextual: app level = Home/Jobs/Activations/Devices,
 * event level = Overview/Gallery/Capture/Media/Sharing.
 * Spec: docs/design-system/COMPONENTS.md.
 */

export interface DockItem {
    key: string;
    title: string;
    icon: React.ReactNode;
    active?: boolean;
    badge?: number;
    onClick?: () => void;
}

interface DockProps {
    items: DockItem[];
    actions?: DockItem[];
}

function DockButton({ item }: { item: DockItem }) {
    return (
        <button
            type="button"
            title={item.title}
            aria-label={item.title}
            aria-current={item.active ? 'page' : undefined}
            onClick={item.onClick}
            className={
                'relative flex h-10 w-10 items-center justify-center rounded-full border-0 bg-transparent cursor-pointer transition-colors ' +
                (item.active
                    ? 'bg-brand text-white'
                    : 'text-dock-dim hover:text-dock-fg hover:bg-dock-hover')
            }
        >
            {item.icon}
            {item.badge !== undefined && item.badge > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-good px-0.5 text-[9px] font-bold text-white">
                    {item.badge}
                </span>
            )}
        </button>
    );
}

export default function Dock({ items, actions = [] }: DockProps) {
    return (
        <nav
            aria-label="Primary"
            className="fixed bottom-[22px] left-1/2 z-30 flex -translate-x-1/2 items-center gap-0.5 rounded-full bg-dock p-[7px] shadow-[0_10px_34px_-6px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.3)]"
        >
            {items.map((item) => (
                <DockButton key={item.key} item={item} />
            ))}
            {actions.length > 0 && (
                <span className="mx-1 h-5 w-px bg-white/10" aria-hidden />
            )}
            {actions.map((item) => (
                <DockButton key={item.key} item={item} />
            ))}
        </nav>
    );
}
